import { IPurchaseState, IPurchaseStateReducerState } from "@smartrr/shared/entities/PurchaseState";
import { updateMatchInPurchaseStateArray } from "@smartrr/shared/utils/updateMatchInPurchaseStateArray";

import { CustomerSubReducer } from "@customer-app/state/typedCustomerRedux";

//
// A messy TypeORM this is that eager loading can still include deleted items sometimes
// this safeguards against that having happened
//
function cleanCpsList(customerPurchaseState: IPurchaseState[]) {
  return customerPurchaseState.filter(cps => !cps.deletedAt).map(clearDeletedCpsLineItems);
}

function clearDeletedCpsLineItems(customerPurchaseState: IPurchaseState) {
  return {
    ...customerPurchaseState,
    stLineItems: customerPurchaseState.stLineItems.filter(lineItem => !lineItem.deletedAt),
  };
}

const initialState: IPurchaseStateReducerState = {
  isInitialized: false,
  isLoading: true,
  nextShipmentDateLoading: false,
  skipLoading: null,
  isSkipLoading: false,
  isPauseLoading: false,
  isCancelLoading: false,
  purchaseStates: [],
  unavailableVariantsAndDeliveries: {
    vnts: [],
    deliveries: [],
    purchasables: [],
  },
};

export const purchaseStatesReducer: CustomerSubReducer<IPurchaseStateReducerState> = (
  state = initialState,
  action
) => {
  switch (action.type) {
    case "LOADING_PURCHASE_STATES": {
      return {
        ...state,
        isLoading: true,
      };
    }

    case "ERROR_LOADING_PURCHASE_STATES": {
      return {
        ...state,
        isLoading: false,
      };
    }

    case "LOADED_UNAVAILABLE_VARIANTS_AND_DELIVERIES": {
      return {
        ...state,
        unavailableVariantsAndDeliveries: action.payload.unavailableVariantsAndDeliveries,
      };
    }

    case "LOADED_PURCHASE_STATES": {
      return {
        ...state,
        isInitialized: true,
        isLoading: false,
        purchaseStates: cleanCpsList(action.payload.purchaseStates),
      };
    }

    case "SKIP_DELIVERY_LOADING":
    case "UNSKIP_DELIVERY_LOADING": {
      return {
        ...state,
        isSkipLoading: true,
        skipLoading: action.payload.date,
      };
    }

    case "SKIP_DELIVERY_ERROR":
    case "UNSKIP_DELIVERY_ERROR": {
      return {
        ...state,
        isSkipLoading: true,
        skipLoading: null,
      };
    }

    case "SKIP_DELIVERY_SUCCESS":
    case "UNSKIP_DELIVERY_SUCCESS": {
      return {
        ...state,
        skipLoading: null,
        isSkipLoading: true,
        purchaseStates: cleanCpsList(
          updateMatchInPurchaseStateArray(
            state.purchaseStates,
            clearDeletedCpsLineItems(action.payload.purchaseState),
            ({ id }) => id,
            "replace"
          )
        ),
      };
    }

    case "STOP_DELIVERY_LOADING": {
      return {
        ...state,
        isSkipLoading: false,
      };
    }

    case "PURCHASE_STATE_PAUSE_LOADING":
    case "PURCHASE_STATE_UNPAUSE_LOADING": {
      return {
        ...state,
        isPauseLoading: true,
      };
    }

    case "PURCHASE_STATE_CANCEL_LOADING": {
      return {
        ...state,
        isCancelLoading: true,
      };
    }
    case "PURCHASE_STATE_CANCEL_ERROR": {
      return {
        ...state,
        isCancelLoading: false,
      };
    }

    case "PURCHASE_STATE_PAUSE_ERROR":
    case "PURCHASE_STATE_UNPAUSE_ERROR": {
      return {
        ...state,
        isPauseLoading: false,
      };
    }

    case "PURCHASE_STATE_CANCEL_SUCCESS": {
      return {
        ...state,
        isCancelLoading: false,
        purchaseStates: cleanCpsList(
          updateMatchInPurchaseStateArray(
            state.purchaseStates,
            clearDeletedCpsLineItems(action.payload.purchaseState),
            ({ id }) => id,
            "newValue"
          )
        ),
      };
    }

    case "PURCHASE_STATE_PAUSE_SUCCESS":
    case "PURCHASE_STATE_UNPAUSE_SUCCESS": {
      return {
        ...state,
        isPauseLoading: false,
        purchaseStates: cleanCpsList(
          updateMatchInPurchaseStateArray(
            state.purchaseStates,
            clearDeletedCpsLineItems(action.payload.purchaseState),
            ({ id }) => id,
            "newValue"
          )
        ),
      };
    }

    case "PURCHASE_STATE_UPDATE_SELLING_PLAN_LOADING":
    case "ORDER_NOW_LOADING":
    case "SET_NEXT_SHIPMENT_DATE_LOADING": {
      return {
        ...state,
        nextShipmentDateLoading: true,
      };
    }

    case "PURCHASE_STATE_UPDATE_SELLING_PLAN_ERROR":
    case "ORDER_NOW_ERROR":
    case "SET_NEXT_SHIPMENT_DATE_ERROR": {
      return {
        ...state,
        nextShipmentDateLoading: false,
      };
    }

    case "PURCHASE_STATE_UPDATE_SELLING_PLAN_SUCCESS":
    case "ORDER_NOW_SUCCESS":
    case "SET_NEXT_SHIPMENT_DATE_SUCCESS": {
      return {
        ...state,
        nextShipmentDateLoading: false,
        purchaseStates: cleanCpsList(
          updateMatchInPurchaseStateArray(
            state.purchaseStates,
            clearDeletedCpsLineItems(action.payload.purchaseState),
            ({ id }) => id,
            "replace"
          )
        ),
      };
    }

    case "PURCHASE_STATE_CREATE_SUCCESS":
    case "PURCHASE_STATE_LINE_ITEM_ADD_SUCCESS":
    case "PURCHASE_STATE_LINE_ITEM_SWAP_SUCCESS":
    case "PURCHASE_STATE_LINE_ITEM_UPDATE_SUCCESS":
    case "PURCHASE_STATE_LINE_ITEM_REMOVE_SUCCESS":
    case "PURCHASE_STATE_UPDATE_ADDRESS_SUCCESS":
    case "PURCHASE_STATE_APPLY_DISCOUNT_CODE_SUCCESS":
    case "PURCHASE_STATE_REMOVE_DISCOUNT_CODE_SUCCESS":
    case "UPDATE_CUSTOMER_PURCHASE_STATE": {
      return {
        ...state,
        isLoading: false,
        purchaseStates: cleanCpsList(
          updateMatchInPurchaseStateArray(
            state.purchaseStates,
            clearDeletedCpsLineItems(action.payload.purchaseState),
            ({ id }) => id,
            "replace"
          )
        ),
      };
    }

    case "UPDATE_CUSTOMER_PURCHASE_STATES": {
      return {
        ...state,
        nextShipmentDateLoading: false,
        purchaseStates: cleanCpsList(action.payload.newPurchaseStates).reduce(
          (cpsStates, cps) => updateMatchInPurchaseStateArray(cpsStates, cps, ({ id }) => id, "newValue"),
          state.purchaseStates
        ),
      };
    }

    case "SEND_AS_GIFT_SUCCESS": {
      return {
        ...state,
        purchaseStates: state.purchaseStates.map(cps => {
          if (cps.id === action.payload.gift.stId) {
            return {
              ...cps,
              gifts: [
                ...(cps.gifts ?? []).filter(gift => gift.skdIdx !== action.payload.gift.skdIdx),
                action.payload.gift,
              ],
            };
          }
          return cps;
        }),
      };
    }

    case "DELETE_GIFT_SUCCESS": {
      return {
        ...state,
        purchaseStates: state.purchaseStates.map(cps => {
          if (cps.id === action.payload.stId) {
            return {
              ...cps,
              gifts: (cps.gifts ?? []).filter(gift => gift.skdIdx !== action.payload.skdIdx),
            };
          }
          return cps;
        }),
      };
    }

    default: {
      return state;
    }
  }
};

export { cleanCpsList as cleanCpsListForTests };
